import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import React, { useContext, useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import Caption from 'LuxKit/Typography/Caption'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import LEOfferPriceDetails, { type LEOfferPricingComponent } from './LEOfferPriceDetails'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusHiddenOfferCarouselCard from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferCarouselCard'
import { connect } from 'react-redux'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { take } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyStayUpgradeBenefitPill from 'loyalty/components/LoyaltyStayUpgradeBenefitPill'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import GeoContext from 'contexts/geoContext'
import ContentTagLabels from '../../Content/ContentTagLabels'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import useExperiment from 'hooks/Experiments/useExperiment'
import { Experiments } from 'constants/experiments'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'
import TheresNoPlaceLikeHomeCampaignLabel from 'components/Common/Labels/TheresNoPlaceLikeHomeCampaignLabel'
import { useShowNoPlaceLikeHomeLabel } from 'hooks/useShowNoPlaceLikeHomeLabel'
import { useRebrandOverlayBookmarkProps } from 'hooks/useRebrandOverlayButtonProps'
import HomepageCarouselContext from 'contexts/OfferList/HomepageCarouselContext'
import { formatNearbyPlaceDistance } from 'components/OfferPage/NavigableOfferContent/formatNearbyPlaceDistance'

interface MappedProps {
  isLuxPlusEnabled: boolean
  isLuxPlusHiddenOffer: boolean
  inclusionsViewableLuxPlusTier: App.MembershipTier
}

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends MappedProps, InheritedCarouselCardMediumProps {
  offer: App.HotelOffer | App.HotelOfferSummary
  onClick?: (isRightClick?: boolean) => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  distance?: number
  distanceFrom?: string
  PricingComponent?: LEOfferPricingComponent
  CtaComponent?: React.ComponentType
}

const DISTANCE_SEPARATOR = '·'

function formatDistanceAway(intl: IntlShape, distanceMeters: number, regionCode: string) {
  return intl.formatMessage(
    {
      id: 'dGThnkp0-f8yzsy5SnOV',
      defaultMessage: '{distance} away',
      description: 'Distance away label shown beside the offer location on nearby recommendation cards',
    },
    {
      distance: formatNearbyPlaceDistance(intl, distanceMeters, regionCode),
    },
  )
}

function getLocationWithDistance(
  intl: IntlShape,
  location: string,
  distanceMeters: number | undefined,
  distanceFrom: string | undefined,
  regionCode: string,
) {
  if (distanceMeters === undefined || !distanceFrom) {
    return location
  }

  const distanceLabel = formatDistanceAway(intl, distanceMeters, regionCode)
  return <Group direction="horizontal" verticalAlign="center" gap={4}>
    <Caption colour="normal-contrast" variant="large" lineClamp={1}>
      {location}
    </Caption>
    <Caption colour="light-foreground-alt" variant="medium">
      {DISTANCE_SEPARATOR}
    </Caption>
    <Caption colour="light-foreground-alt" variant="medium" lineClamp={1}>
      {distanceLabel}
    </Caption>
  </Group>
}

function LEOfferCard(props: Props) {
  const intl = useIntl()
  const {
    offer,
    size,
    onClick,
    className,
    offerUrl,
    isLuxPlusHiddenOffer,
    isLuxPlusEnabled,
    selectedDatesAndRoomsFilter,
    inclusionsViewableLuxPlusTier,
    variant,
    eagerLoadImage,
    distance,
    distanceFrom,
    PricingComponent,
    CtaComponent,
  } = props
  const isHomepageCarousel = useContext(HomepageCarouselContext)
  const { iconKind, iconVariant } = useRebrandOverlayBookmarkProps()
  const { lowestPricePackage } = offer
  const isUltraLux = isUltraLuxOffer(offer)
  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const luxPlusInclusions = lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier as App.MembershipSubscriptionTier]
  const offerLabels = useMemo(() => offer.urgencyTags.filter(label => label.type !== 'left'), [offer])

  const isFlashHotel = isFlashHotelOffer(offer)
  const { isAnyVariant: isLtleHomepageEnabled } = useExperiment(Experiments.croDisplayLtleOnHomepage, { canActivate: isFlashHotel })
  const shouldShowFlashInclusions = isFlashHotel

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter || {})
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const inclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.tileInclusions?.length) {
      return offer.tileInclusions
    }
    if (offer.lowestPricePackage?.inclusions?.length) {
      return offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
    }
    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    return pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate') || []
  }, [offer])

  const loyaltyCount = useLoyaltyBenefitInclusions(offer).length
  const showLoyaltyInline = inclusions.length < 3

  const shortInclusions = useMemo(() => take(inclusions, 3), [inclusions])

  const { currentRegionCode } = useContext(GeoContext)
  const location = useMemo(() => {
    const location = [offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')
    return getLocationWithDistance(intl, location, distance, distanceFrom, currentRegionCode)
  }, [currentRegionCode, distance, distanceFrom, intl, offer.locationHeading, offer.locationSubheading])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (offer.bundledWithFlightsOnly) {
      return []
    }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: isHotelPackage(pkg) ? pkg : undefined,
        price: pkg.price,
        memberPrice: pkg.memberPrice,
        currentRegionCode,
        nights: pkg.duration,
      }),
    ]
  }, [offer, pkg, currentRegionCode])

  const hasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  // Check if this is a charter bundle offer
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])

  const charterBundleImageLabel = isCharterBundleOffer
    ? (
        <NeutralLabel>
          {intl.formatMessage({
            id: 'PjmdhR5JAQXM7v4WOevW',
            defaultMessage: 'Hotel & Flight bundle',
            description: 'Label for charter bundle offers combining hotel and flight',
          })}
        </NeutralLabel>
      )
    : undefined

  const productLabel = useMemo(() => {
    if (isFlashHotel && isLtleHomepageEnabled && !isCharterBundleOffer) {
      return <LtleTooltipWrapper variant="light"><ProductTypeLabel productType={offer.productType} /></LtleTooltipWrapper>
    }
    return undefined
  }, [isFlashHotel, isLtleHomepageEnabled, isCharterBundleOffer, offer.productType])

  let urgencyTagLabel: React.ReactElement | undefined = undefined
  const urgencyTagAlreadyActive = offerLabels.find((ol: App.OfferUrgencyLabel) => ol.type == 'left' && ol.end || ol.type == 'lux_plus_general_release')
  if (offer?.visibilitySchedule?.end && !urgencyTagAlreadyActive) {
    urgencyTagLabel = <OfferUrgencyCountdownLabel
      endDate={offer?.visibilitySchedule?.end}
      tagPlace="offer-card"
      fallback={size === 'medium' && isUltraLux ? <ProductTypeLabel productType={offer.productType} /> : undefined}
    />
  }
  const showTheresNoPlaceLikeHomeLabel = useShowNoPlaceLikeHomeLabel(offer.campaignTags)

  const hasInclusions = shortInclusions.length > 0
  const remainingInclusionsCount = inclusions.length - shortInclusions.length + (showLoyaltyInline ? 0 : loyaltyCount)
  const showInclusionsInsteadOfTagline = shouldShowFlashInclusions && hasInclusions
  const { isAnyVariant: isMarketingHeadlineVariant } = useExperiment(
    Experiments.croHomepageCarouselMarketingHeadlineV2,
    { canActivate: !!isHomepageCarousel && showInclusionsInsteadOfTagline },
  )
  const showMarketingHeadlineWithInclusions = !!isHomepageCarousel && isMarketingHeadlineVariant && showInclusionsInsteadOfTagline

  const inclusionsElement = (shouldShowFlashInclusions && hasInclusions) ? <Group
    gap={8}
    tabletGap={8}
    direction="vertical"
    tabletDirection="horizontal"
    horizontalAlign="start"
    wrap="wrap"
    style={{ paddingBottom: 8 }}
  >
    {shortInclusions.map((inclusion) => <OfferInclusionItem
      key={inclusion.id}
      inclusion={inclusion}
      size="medium"
      isHighlighted={false}
    />)}
    {showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="offer-page" />}
    {remainingInclusionsCount > 0 && (
      <BodyText variant="medium" underline>
        {intl.formatMessage(
          {
            id: '-_Y7PlvvzQaDdQJ6J66z',
            defaultMessage: '+{count} more',
            description: 'Text indicating additional inclusions count on offer card',
          },
          { count: remainingInclusionsCount },
        )}
      </BodyText>
    )}
  </Group> : undefined
  const flashTileDescription = showInclusionsInsteadOfTagline && !showMarketingHeadlineWithInclusions ? undefined : offer.name

  return (
    <ProductPaletteProvider product={offer}>
      {isLuxPlusHiddenOffer && <LuxPlusHiddenOfferCarouselCard className={className} urgencyLabels={offerLabels} offer={offer} size={size} />}
      {!isLuxPlusHiddenOffer && <>
        {size === 'medium' && <CarouselCardMedium
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={location}
          providerName={offer.property.name}
          title={showInclusionsInsteadOfTagline && !showMarketingHeadlineWithInclusions ? '' : offer.name}
          priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} PricingComponent={PricingComponent} CtaComponent={CtaComponent} />}
          to={offerUrl}
          productLabel={productLabel}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          urgencyLabels={<LabelGroup>
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
              suppressTheresNoPlaceLikeHomeCampaignLabel
            />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
            {urgencyTagLabel}
            {showTheresNoPlaceLikeHomeLabel && <TheresNoPlaceLikeHomeCampaignLabel />}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          titleVariant={showInclusionsInsteadOfTagline ? 'medium' : 'large'}
          onClick={onClick}
        />}
        {size === 'small' && <CarouselCardSmall
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" iconKind={iconKind} iconVariant={iconVariant} />}
          description={flashTileDescription}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={location}
          title={offer.property.name}
          priceDetails={<LEOfferPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} PricingComponent={PricingComponent} CtaComponent={CtaComponent} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          urgencyLabels={<LabelGroup>
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
              suppressTheresNoPlaceLikeHomeCampaignLabel
            />
            {urgencyTagLabel}
            {showTheresNoPlaceLikeHomeLabel && <TheresNoPlaceLikeHomeCampaignLabel />}
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          onClick={onClick}
        />}
      </>}
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>): MappedProps {
  return ({
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLuxPlusHiddenOffer: getIsLuxPlusHiddenOffer(state, ownProps.offer),
    inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(state),
  })
}

export default connect(mapStateToProps)(LEOfferCard)
